import {
  Column,
  FormField,
  UserTableSchema,
} from '@freesixtyfive/fe-user-management';
import { _ } from '@ngx-translate/core';
import * as z from 'zod';

// api: tinyint <-> form: boolean
const bitToBoolean = z.codec(z.number(), z.boolean(), {
  decode: (nVal) => !!nVal,
  encode: (bVal) => +bVal,
});

// shape of UserView (api.user-management); fields not in the form optional for encode
const ManagedUserSchema = z.object({
  id: z.string(),
  email: z.email(),
  salutation: z.string().nullable(),
  active: bitToBoolean,
  name: z.string(),
  title: z.string().nullable(),
  createdAt: z.string().optional(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  companyName: z.string().nullable().optional(),
  companyId: z.number().nullable().optional(),
  roles: z.string().array(),
  hasPassword: z.boolean().optional(),
});

type ManagedUser = z.infer<typeof ManagedUserSchema>;

const columns: Column<keyof ManagedUser>[] = [
  {
    header: 'column.email',
    field: 'email',
    filterField: 'email',
    sortField: 'email',
    type: 'text',
  },
  {
    header: 'column.roles',
    field: 'roles',
    filterField: 'roles',
    type: 'text',
    displayType: 'tags',
    placeholder: 'placeholder.selectRole',
  },
  {
    header: 'column.active',
    field: 'active',
    filterField: 'active',
    sortField: 'active',
    type: 'boolean',
  },
  {
    header: 'column.firstName',
    field: 'firstName',
    filterField: 'firstName',
    sortField: 'firstName',
    type: 'text',
  },
  {
    header: 'column.lastName',
    field: 'lastName',
    filterField: 'lastName',
    sortField: 'lastName',
    type: 'text',
  },
  {
    header: 'column.createdAt',
    field: 'createdAt',
    filterField: 'created_at',
    sortField: 'created_at',
    type: 'date',
    displayType: 'date',
  },
];

const formFields: FormField<keyof ManagedUser>[] = [
  { field: 'email', header: 'column.email', type: 'text' },
  {
    field: 'salutation',
    header: 'column.salutation',
    type: 'select',
    options: [
      { label: 'salutation.mr', value: 'mr' },
      { label: 'salutation.ms', value: 'ms' },
      { label: 'salutation.diverse', value: 'diverse' },
    ],
  },
  {
    field: 'title',
    header: 'column.title',
    type: 'select',
    options: [
      { label: 'Dr.', value: 'Dr.' },
      { label: 'Prof.', value: 'Prof.' },
      { label: 'Prof. Dr.', value: 'Prof. Dr.' },
    ],
  },
  { field: 'firstName', header: 'column.firstName', type: 'text' },
  { field: 'lastName', header: 'column.lastName', type: 'text' },
  { field: 'active', header: 'column.active', type: 'boolean' },
  {
    field: 'roles',
    header: 'column.roles',
    type: 'tags',
    // relative to /api -> roles-permissions index
    optionsEndpoint: 'roles',
  },
];

const globalFilterFields = ['email', 'firstName', 'lastName'];

_([
  'column.active',
  'column.createdAt',
  'column.email',
  'column.firstName',
  'column.lastName',
  'column.roles',
  'column.salutation',
  'column.title',
  'placeholder.selectRole',
  'salutation.diverse',
  'salutation.mr',
  'salutation.ms',
]);

const ManagedUserWithMeta = ManagedUserSchema.meta({
  columns,
  formFields,
  globalFilterFields,
  emptyRecord: (): ManagedUser => ({
    id: '',
    name: '',
    email: '',
    firstName: '',
    lastName: '',
    salutation: null,
    title: null,
    active: true,
    roles: [],
  }),
});

// name required by api, derived from first/last name
const ManagedUser: UserTableSchema = {
  safeParse: (data) => ManagedUserWithMeta.safeParse(data),
  safeEncode: (data) => {
    const record = data as Partial<ManagedUser>;
    const name = [record.firstName, record.lastName]
      .filter((part) => !!part?.trim())
      .join(' ');
    return ManagedUserWithMeta.safeEncode({ ...record, name } as ManagedUser);
  },
  meta: () => ManagedUserWithMeta.meta(),
};

export { ManagedUser };
